'use client'

import { createContext, useContext, useCallback, useEffect, useState } from 'react'

export type Language = 'en' | 'es'

interface LanguageContextValue {
  lang: Language
  setLanguage: (l: Language) => void
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: 'en',
  setLanguage: () => {},
})

export function useLanguage() {
  return useContext(LanguageContext)
}

function applyCookie(lang: Language) {
  document.cookie = `gg-lang=${lang};path=/;max-age=31536000;samesite=lax`
}

function applyToDoc(lang: Language) {
  document.documentElement.setAttribute('lang', lang)
}

interface LanguageProviderProps {
  initialLang: Language
  userEmail?: string | null
  children: React.ReactNode
}

export default function LanguageProvider({ initialLang, userEmail, children }: LanguageProviderProps) {
  const [lang, setLangState] = useState<Language>(initialLang)

  // Sync html[lang] whenever language changes
  useEffect(() => {
    applyToDoc(lang)
  }, [lang])

  // On mount: if user is known, load their saved language from KV (may override cookie)
  useEffect(() => {
    if (!userEmail) return
    fetch(`/api/user/language?email=${encodeURIComponent(userEmail)}`)
      .then(r => r.json())
      .then(({ lang: saved }) => {
        if (saved && saved !== lang) {
          setLangState(saved as Language)
          applyToDoc(saved as Language)
          applyCookie(saved as Language)
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail])

  const setLanguage = useCallback((l: Language) => {
    setLangState(l)
    applyToDoc(l)
    applyCookie(l)
    if (userEmail) {
      fetch('/api/user/language', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, lang: l }),
      }).catch(() => {})
    }
  }, [userEmail])

  return (
    <LanguageContext.Provider value={{ lang, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}
